import React, { useState, useEffect, useCallback } from 'react';
import { AlertCircle, Plus, Search, Calendar, Clock, CheckCircle, XCircle, AlertTriangle, FileText, MapPin, Phone } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import mockApi from '../services/mockApi';

const ClienteAveriasPage = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [averias, setAverias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState('todas');
  const [busqueda, setBusqueda] = useState('');
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [nuevaAveria, setNuevaAveria] = useState({
    descripcion: '',
    ubicacion: '',
    kilometraje: ''
  });

  const cargarAverias = useCallback(async () => {
    try {
      setLoading(true);
      const clienteRes = await mockApi.getClienteByEmail(user.email);
      const response = await mockApi.getAveriasCliente(clienteRes.data.id);
      setAverias(response.data || []);
    } catch (error) {
      console.error('Error al cargar averías:', error);
      setAverias([]);
    } finally {
      setLoading(false);
    }
  }, [user.email]);

  useEffect(() => {
    if (location.state?.abrirFormulario) {
      setMostrarFormulario(true);
    }
  }, [location]);

  useEffect(() => {
    cargarAverias();
  }, [cargarAverias]);

  const handleReportarAveria = async (e) => {
    e.preventDefault();
    try {
      const clienteRes = await mockApi.getClienteByEmail(user.email);
      await mockApi.reportarAveria(clienteRes.data.id, nuevaAveria);
      alert('Avería reportada correctamente. Nos pondremos en contacto contigo pronto.');
      setMostrarFormulario(false);
      setNuevaAveria({ descripcion: '', ubicacion: '', kilometraje: '' });
      cargarAverias();
    } catch (error) {
      console.error('Error al reportar avería:', error);
      alert('Error al reportar la avería');
    }
  };

  const averiasFiltradas = averias.filter(averia => {
    const cumpleFiltro = filtroEstado === 'todas' || averia.estado === filtroEstado;
    const cumpleBusqueda = busqueda === '' ||
      averia.descripcion.toLowerCase().includes(busqueda.toLowerCase()) ||
      averia.numeroAveria.toLowerCase().includes(busqueda.toLowerCase());
    return cumpleFiltro && cumpleBusqueda;
  });

  const estadisticas = {
    total: averias.length,
    activas: averias.filter(a => ['reportada', 'en_revision', 'aprobada', 'en_reparacion'].includes(a.estado)).length,
    finalizadas: averias.filter(a => a.estado === 'finalizada').length,
    rechazadas: averias.filter(a => a.estado === 'rechazada').length
  };

  const getEstadoConfig = (estado) => {
    const configs = {
      reportada: { color: 'bg-blue-100 text-blue-700', icon: AlertCircle, label: 'Reportada' },
      en_revision: { color: 'bg-yellow-100 text-yellow-700', icon: Clock, label: 'En Revisión' },
      aprobada: { color: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'Aprobada' },
      rechazada: { color: 'bg-red-100 text-red-700', icon: XCircle, label: 'Rechazada' },
      en_reparacion: { color: 'bg-purple-100 text-purple-700', icon: AlertTriangle, label: 'En Reparación' },
      finalizada: { color: 'bg-gray-100 text-gray-700', icon: CheckCircle, label: 'Finalizada' }
    };
    return configs[estado] || configs.reportada;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-black bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
              Mis Averías
            </h1>
            <p className="text-gray-600 mt-1">Gestiona y consulta el estado de tus averías</p>
          </div>
          <button
            onClick={() => setMostrarFormulario(!mostrarFormulario)}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-danger-500 to-danger-600 text-white rounded-xl hover:shadow-lg transition-all duration-300 transform hover:scale-105"
          >
            <Plus className="w-5 h-5" />
            <span className="font-bold">Reportar Avería</span>
          </button>
        </div>

        {mostrarFormulario && (
          <div className="glass-effect rounded-2xl p-6 border-l-4 border-danger-500">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Nueva Avería</h2>
            <form onSubmit={handleReportarAveria} className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Descripción de la Avería *
                </label>
                <textarea
                  value={nuevaAveria.descripcion}
                  onChange={(e) => setNuevaAveria({ ...nuevaAveria, descripcion: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  rows="4"
                  placeholder="Describe detalladamente el problema..."
                  required
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Ubicación Actual
                  </label>
                  <input
                    type="text"
                    value={nuevaAveria.ubicacion}
                    onChange={(e) => setNuevaAveria({ ...nuevaAveria, ubicacion: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Ciudad o dirección"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Kilometraje Actual
                  </label>
                  <input
                    type="number"
                    value={nuevaAveria.kilometraje}
                    onChange={(e) => setNuevaAveria({ ...nuevaAveria, kilometraje: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Ej: 45000"
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-danger-500 to-danger-600 text-white rounded-xl hover:shadow-lg transition-all duration-300 font-bold"
                >
                  Enviar Reporte
                </button>
                <button
                  type="button"
                  onClick={() => setMostrarFormulario(false)}
                  className="px-6 py-3 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-all duration-300 font-bold"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="glass-effect rounded-2xl p-6 border-l-4 border-primary-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-semibold">Total Averías</p>
                <p className="text-3xl font-black text-gray-900 mt-1">{estadisticas.total}</p>
              </div>
              <AlertCircle className="w-10 h-10 text-primary-500" />
            </div>
          </div>
          <div className="glass-effect rounded-2xl p-6 border-l-4 border-warning-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-semibold">Activas</p>
                <p className="text-3xl font-black text-gray-900 mt-1">{estadisticas.activas}</p>
              </div>
              <Clock className="w-10 h-10 text-warning-500" />
            </div>
          </div>
          <div className="glass-effect rounded-2xl p-6 border-l-4 border-success-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-semibold">Finalizadas</p>
                <p className="text-3xl font-black text-gray-900 mt-1">{estadisticas.finalizadas}</p>
              </div>
              <CheckCircle className="w-10 h-10 text-success-500" />
            </div>
          </div>
          <div className="glass-effect rounded-2xl p-6 border-l-4 border-danger-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-semibold">Rechazadas</p>
                <p className="text-3xl font-black text-gray-900 mt-1">{estadisticas.rechazadas}</p>
              </div>
              <XCircle className="w-10 h-10 text-danger-500" />
            </div>
          </div>
        </div>

        <div className="glass-effect rounded-2xl p-6">
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Buscar por número o descripción..."
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setFiltroEstado('todas')}
                className={`px-4 py-3 rounded-xl font-bold transition-all ${
                  filtroEstado === 'todas'
                    ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Todas
              </button>
              <button
                onClick={() => setFiltroEstado('en_reparacion')}
                className={`px-4 py-3 rounded-xl font-bold transition-all ${
                  filtroEstado === 'en_reparacion'
                    ? 'bg-gradient-to-r from-warning-500 to-warning-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Activas
              </button>
              <button
                onClick={() => setFiltroEstado('finalizada')}
                className={`px-4 py-3 rounded-xl font-bold transition-all ${
                  filtroEstado === 'finalizada'
                    ? 'bg-gradient-to-r from-success-500 to-success-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Finalizadas
              </button>
            </div>
          </div>

          <div className="space-y-4">
            {averiasFiltradas.length === 0 ? (
              <div className="text-center py-12">
                <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 font-semibold">No se encontraron averías</p>
              </div>
            ) : (
              averiasFiltradas.map((averia) => {
                const estadoConfig = getEstadoConfig(averia.estado);
                const IconoEstado = estadoConfig.icon;

                return (
                  <div key={averia.id} className="glass-effect rounded-xl p-6 hover:shadow-lg transition-all duration-300">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-start gap-4 flex-1">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${estadoConfig.color}`}>
                          <IconoEstado className="w-6 h-6" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-bold text-gray-900">{averia.numeroAveria}</h3>
                            <span className={`px-3 py-1 rounded-full text-xs font-bold ${estadoConfig.color}`}>
                              {estadoConfig.label}
                            </span>
                          </div>
                          <p className="text-gray-700 mb-3">{averia.descripcion}</p>
                          <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4" />
                              <span>Reportada: {new Date(averia.fechaReporte).toLocaleDateString()}</span>
                            </div>
                            {averia.taller && (
                              <div className="flex items-center gap-2">
                                <MapPin className="w-4 h-4" />
                                <span>{averia.taller}</span>
                              </div>
                            )}
                            {averia.costoEstimado && (
                              <div className="flex items-center gap-2">
                                <FileText className="w-4 h-4" />
                                <span>Coste: {averia.costoEstimado}€</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {averia.observaciones && (
                      <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <p className="text-sm font-semibold text-blue-900 mb-1">Observaciones:</p>
                        <p className="text-sm text-blue-700">{averia.observaciones}</p>
                      </div>
                    )}

                    {averia.estado === 'rechazada' && averia.motivoRechazo && (
                      <div className="mt-4 p-4 bg-red-50 rounded-lg border border-red-200">
                        <p className="text-sm font-semibold text-red-900 mb-1">Motivo de Rechazo:</p>
                        <p className="text-sm text-red-700">{averia.motivoRechazo}</p>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div className="glass-effect rounded-2xl p-6 bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200">
          <div className="flex items-start gap-4">
            <Phone className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-blue-900 mb-2">¿Necesitas ayuda?</h3>
              <p className="text-sm text-blue-700 mb-3">
                Si tienes alguna duda sobre el estado de tu avería o necesitas asistencia inmediata,
                no dudes en contactarnos.
              </p>
              <div className="flex gap-4 text-sm">
                <div>
                  <span className="font-semibold text-blue-900">Teléfono:</span>
                  <span className="text-blue-700 ml-2">900 123 456</span>
                </div>
                <div>
                  <span className="font-semibold text-blue-900">Email:</span>
                  <span className="text-blue-700 ml-2">soporte@rdgarantia.com</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ClienteAveriasPage;
